import { Component } from '@theme/component';

/**
 * A shared countdown timer.
 *
 * Renders the remaining days / hours / minutes / seconds until a target
 * date/time supplied via the `datetime` attribute. A single target (stored in
 * theme settings) drives every placement — Hero, Announcement Bar and
 * Full-Width Banner — so they all stay in sync.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 *
 * @extends {Component<Refs>}
 */
export class CountdownTimer extends Component {
  /** @type {number|undefined} */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.#tick();
    this.#start();
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * The target date, or `null` when the `datetime` attribute is missing or invalid.
   * @returns {Date|null}
   */
  get target() {
    const value = this.getAttribute('datetime');
    if (!value) return null;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  #start() {
    if (this.#interval || !this.target) return;
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
    } else {
      this.#tick();
      this.#start();
    }
  };

  #tick() {
    const { target } = this;

    if (!target) {
      this.setAttribute('hidden', '');
      return;
    }

    const diff = target.getTime() - Date.now();

    if (diff <= 0) {
      this.setAttribute('data-expired', '');
      this.setAttribute('hidden', '');
      this.#stop();
      this.#paint(0, 0, 0, 0);
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    this.#paint(
      Math.floor(totalSeconds / 86400),
      Math.floor((totalSeconds % 86400) / 3600),
      Math.floor((totalSeconds % 3600) / 60),
      totalSeconds % 60,
    );
  }

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  #paint(days, hours, minutes, seconds) {
    this.#set(this.refs.days, days);
    this.#set(this.refs.hours, hours);
    this.#set(this.refs.minutes, minutes);
    this.#set(this.refs.seconds, seconds);
  }

  /**
   * @param {Element|undefined} el
   * @param {number} value
   */
  #set(el, value) {
    if (el instanceof HTMLElement) {
      el.textContent = `${value}`.padStart(2, '0');
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
